import ArrowDown from "@mui/icons-material/KeyboardArrowDown";
import ArrowUp from "@mui/icons-material/KeyboardArrowUp";
import { formatInTimeZone } from "date-fns-tz";
import { Feature, GeoJsonProperties, Geometry } from "geojson";
import { useEffect, useMemo, useRef } from "react";

import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";
import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";
import useSelectedMarker from "@/features/shared/widgets/map/settings/use-selected-marker";
import { SeismicEventPropertyOptions } from "@/features/shared/widgets/map/map.types";

import getSeismicColors from "./get-seismic-colors";
import {
  SeismicOverlayList,
  SeismicOverlayListItem,
  SeismicOverlayListItemBody,
  SeismicOverlayListItemHeader,
  SeismicOverlayListItemHeaderColor,
  SeismicOverlayListItemHeaderInfo,
  SeismicOverlayListItemHeaderInfoLocation,
  SeismicOverlayListItemHeaderStack,
} from "./seismic-events-overlay.styles";

interface SeismicEventsOverlayProps {
  availableEvents: Feature<Geometry, GeoJsonProperties>[];
  currentSettingValues: LayerSettingValueSet;
  handleSelect: (event: Feature<Geometry, GeoJsonProperties>) => void;
  handleClose: () => void;
}

const SeismicEventsOverlay = ({
  availableEvents,
  currentSettingValues,
  handleSelect,
}: SeismicEventsOverlayProps) => {
  const { selectedMarker } = useSelectedMarker();

  const selectedProperty = useMemo(() => {
    if (!currentSettingValues?.seismicEventsType) return undefined;
    return currentSettingValues.seismicEventsType.value as SeismicEventPropertyOptions;
  }, [currentSettingValues]);

  return (
    <SeismicOverlayList>
      {availableEvents.map((event) => (
        <SeismicOverlayItem
          key={event.id}
          event={event}
          selectedProperty={selectedProperty}
          onClick={handleSelect}
          isActive={selectedMarker?.value === event.id}
        />
      ))}
    </SeismicOverlayList>
  );
};

interface SeismicOverlayItemProps {
  event: Feature;
  selectedProperty?: SeismicEventPropertyOptions;
  onClick: (event: Feature<Geometry, GeoJsonProperties>) => void;
  isActive: boolean;
}

export const SeismicOverlayItem = ({
  event,
  selectedProperty = SeismicEventPropertyOptions.AGE,
  onClick,
  isActive,
}: SeismicOverlayItemProps) => {
  const tz = useWidgetTimezone();
  const itemRef = useRef<HTMLDivElement | null>(null);

  const backgroundColor = getSeismicColors(
    selectedProperty,
    event.properties?.time,
    event.properties?.depth,
  );
  const date = new Date(event.properties?.time);
  const timezone = tz?.name === "UTC" ? tz.offset : (tz?.name ?? "");

  useEffect(() => {
    if (isActive && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isActive]);

  const depth = useMemo(() => {
    const parsedDepth = Number(event.properties?.depth);
    if (Number.isNaN(parsedDepth)) return event.properties?.depth;
    return `${parsedDepth.toFixed(0)}km`;
  }, [event.properties?.depth]);

  const latLng = useMemo(() => {
    if (!event.properties?.lat || !event.properties?.lon) return;
    const lat = Number(event.properties.lat);
    const lng = Number(event.properties.lon);
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }, [event.properties?.lat, event.properties?.lng]);

  return (
    <SeismicOverlayListItem ref={itemRef}>
      <SeismicOverlayListItemHeader onClick={() => onClick(event)}>
        <SeismicOverlayListItemHeaderStack>
          <SeismicOverlayListItemHeaderColor
            style={{ backgroundColor }}
          ></SeismicOverlayListItemHeaderColor>
          {event.properties?.mag?.toFixed(1)}
        </SeismicOverlayListItemHeaderStack>
        <SeismicOverlayListItemHeaderInfo>
          <div>{formatInTimeZone(date, timezone, "dd-MM-yyyy HH:mm")}</div>
          <SeismicOverlayListItemHeaderInfoLocation>
            {event.properties?.location}
          </SeismicOverlayListItemHeaderInfoLocation>
        </SeismicOverlayListItemHeaderInfo>
        <div>{isActive ? <ArrowUp /> : <ArrowDown />}</div>
      </SeismicOverlayListItemHeader>
      {isActive && (
        <SeismicOverlayListItemBody>
          <span>Time UTC</span>
          <span>{formatInTimeZone(date, "UTC", "dd-MM-yyyy HH:mm (z)")}</span>
          <span>Location</span>
          <span>{latLng}</span>
          <span>Depth</span>
          <span>{depth}</span>
        </SeismicOverlayListItemBody>
      )}
    </SeismicOverlayListItem>
  );
};

export default SeismicEventsOverlay;
